/**
 * Deliverable Feedback Dialog
 *
 * Captures user feedback for enhancing the entire Deliverables section or individual deliverables.
 * Provides deliverable-specific suggestions and enhancement options.
 */

import React, { useState } from 'react';
import {
  X,
  Sparkles,
  RefreshCw,
  MessageSquare,
  Lightbulb,
  AlertCircle,
  ChevronDown,
  Package,
  Clock,
  FileText,
  Users,
  CheckCircle,
  Target,
} from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

export interface DeliverableFeedback {
  feedbackType: 'section' | 'individual';
  deliverableId?: string;
  deliverableName?: string;
  userFeedback: string;
  enhancementLevel: 'minor' | 'moderate' | 'significant';
  focusAreas: string[];
}

interface DeliverableFeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: DeliverableFeedback) => void;
  mode: 'section' | 'individual';
  deliverableId?: string;
  deliverableName?: string;
  isSubmitting?: boolean;
}

// ============================================================================
// FOCUS AREA OPTIONS
// ============================================================================

const SECTION_FOCUS_AREAS = [
  { id: 'labor_estimates', label: 'Labor Estimates', description: 'Make hour estimates more realistic and defensible', icon: Clock },
  { id: 'specifications', label: 'Specifications', description: 'Add detailed technical requirements and formats', icon: FileText },
  { id: 'compliance', label: 'Compliance Coverage', description: 'Expand regulatory and contractual compliance', icon: CheckCircle },
  { id: 'descriptions', label: 'Descriptions', description: 'Make deliverable descriptions more comprehensive', icon: Package },
  { id: 'acceptance_criteria', label: 'Acceptance Criteria', description: 'Add specific, measurable acceptance criteria', icon: Target },
  { id: 'labor_categories', label: 'Labor Categories', description: 'Refine labor category assignments and breakdowns', icon: Users },
];

const INDIVIDUAL_FOCUS_AREAS = [
  { id: 'hours', label: 'Hour Estimates', description: 'More realistic and defensible hours', icon: Clock },
  { id: 'specs', label: 'Specifications', description: 'Technical requirements and formats', icon: FileText },
  { id: 'compliance', label: 'Compliance', description: 'Regulatory requirements', icon: CheckCircle },
  { id: 'phases', label: 'Labor Phases', description: 'Phase breakdown and activities', icon: Users },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function DeliverableFeedbackDialog({
  isOpen,
  onClose,
  onSubmit,
  mode,
  deliverableId,
  deliverableName,
  isSubmitting = false,
}: DeliverableFeedbackDialogProps) {
  const [feedback, setFeedback] = useState('');
  const [enhancementLevel, setEnhancementLevel] = useState<'minor' | 'moderate' | 'significant'>('moderate');
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(true);

  const isSection = mode === 'section';
  const focusOptions = isSection ? SECTION_FOCUS_AREAS : INDIVIDUAL_FOCUS_AREAS;

  // Quick feedback suggestions
  const getSuggestions = () => {
    if (isSection) {
      return [
        'Make all labor estimates more defensible with industry benchmarks',
        'Add specific acceptance criteria for each deliverable',
        'Expand compliance coverage with FAR/DFARS requirements',
        'Include more detailed technical specifications',
        'Add quality standards and verification methods',
        'Align labor categories with standard GSA schedules',
        'Include risk factors in hour estimates',
        'Add dependencies between deliverables',
      ];
    } else {
      return [
        'Make hour estimates more realistic',
        'Add specific acceptance criteria',
        'Expand technical requirements',
        'Include compliance deliverables',
        'Detail the labor breakdown by phase',
        'Add quality standards',
        'Include format specifications',
        'Add verification methods',
      ];
    }
  };

  const toggleFocusArea = (areaId: string) => {
    setFocusAreas(prev =>
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const handleSubmit = () => {
    if (!feedback.trim() && focusAreas.length === 0) {
      return;
    }

    onSubmit({
      feedbackType: mode,
      deliverableId,
      deliverableName,
      userFeedback: feedback.trim(),
      enhancementLevel,
      focusAreas,
    });
  };

  const handleSuggestionClick = (suggestion: string) => {
    setFeedback(prev => {
      if (prev.trim()) {
        return `${prev.trim()}. ${suggestion}`;
      }
      return suggestion;
    });
  };

  if (!isOpen) return null;

  const title = isSection
    ? 'Enhance Deliverables Section'
    : `Enhance: ${deliverableName || 'Deliverable'}`;

  const subtitle = isSection
    ? 'Improve all deliverables with AI assistance'
    : 'Improve this specific deliverable';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-bw-surface dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-bw-border dark:border-gray-700 bg-bw-surface-subtle dark:from-gray-800 dark:to-gray-750 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-bw-primary-tint dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                <Package className="w-5 h-5 text-bw-primary dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-bw-ink dark:text-white">
                  {title}
                </h2>
                <p className="text-sm text-bw-muted dark:text-bw-muted">
                  {subtitle}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="p-2 text-bw-muted hover:text-bw-muted dark:hover:text-gray-300 hover:bg-bw-surface-subtle dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Focus Areas */}
          <div>
            <label className="block text-sm font-medium text-bw-ink dark:text-gray-300 mb-3">
              <Target className="w-4 h-4 inline mr-2" />
              Enhancement Focus Areas
              <span className="text-bw-muted font-normal ml-2">(Select what to improve)</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {focusOptions.map(area => {
                const Icon = area.icon;
                const isSelected = focusAreas.includes(area.id);
                return (
                  <button
                    key={area.id}
                    type="button"
                    onClick={() => toggleFocusArea(area.id)}
                    disabled={isSubmitting}
                    className={`p-3 rounded-lg border-2 transition-all text-left disabled:opacity-50 ${
                      isSelected
                        ? 'border-indigo-500 bg-bw-primary-tint dark:bg-indigo-900/20'
                        : 'border-bw-border dark:border-gray-600 hover:border-bw-border dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-bw-primary' : 'text-bw-muted'}`} />
                      <span className={`font-medium text-sm ${
                        isSelected ? 'text-bw-primary dark:text-indigo-300' : 'text-bw-ink dark:text-white'
                      }`}>
                        {area.label}
                      </span>
                    </div>
                    <p className="text-xs text-bw-muted dark:text-bw-muted mt-1 ml-6">
                      {area.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Feedback textarea */}
          <div>
            <label className="block text-sm font-medium text-bw-ink dark:text-gray-300 mb-2">
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Additional Instructions
              <span className="text-bw-muted font-normal ml-2">(Optional)</span>
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Tell the AI specific things you'd like to change or improve..."
              rows={3}
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-bw-border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white resize-none disabled:opacity-50"
            />
          </div>

          {/* Quick suggestions */}
          <div>
            <button
              type="button"
              onClick={() => setShowSuggestions(!showSuggestions)}
              className="flex items-center gap-2 text-sm font-medium text-bw-ink dark:text-gray-300 mb-2"
            >
              <Lightbulb className="w-4 h-4 text-fit-moderate" />
              Quick Suggestions
              <ChevronDown className={`w-4 h-4 transition-transform ${showSuggestions ? 'rotate-180' : ''}`} />
            </button>
            {showSuggestions && (
              <div className="flex flex-wrap gap-2">
                {getSuggestions().map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 text-xs bg-bw-surface-subtle dark:bg-gray-700 text-bw-ink dark:text-gray-300 rounded-full hover:bg-bw-primary-tint dark:hover:bg-indigo-900/30 hover:text-bw-primary dark:hover:text-indigo-300 transition-colors disabled:opacity-50"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Enhancement level */}
          <div>
            <label className="block text-sm font-medium text-bw-ink dark:text-gray-300 mb-3">
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Enhancement Level
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'minor', label: 'Minor', description: 'Polish existing content' },
                { value: 'moderate', label: 'Moderate', description: 'Add detail & depth' },
                { value: 'significant', label: 'Significant', description: 'Major expansion' },
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setEnhancementLevel(option.value as typeof enhancementLevel)}
                  disabled={isSubmitting}
                  className={`p-3 rounded-lg border-2 transition-all text-left disabled:opacity-50 ${
                    enhancementLevel === option.value
                      ? 'border-indigo-500 bg-bw-primary-tint dark:bg-indigo-900/20'
                      : 'border-bw-border dark:border-gray-600 hover:border-bw-border dark:hover:border-gray-500'
                  }`}
                >
                  <div className={`font-medium text-sm ${
                    enhancementLevel === option.value
                      ? 'text-bw-primary dark:text-indigo-300'
                      : 'text-bw-ink dark:text-white'
                  }`}>
                    {option.label}
                  </div>
                  <div className="text-xs text-bw-muted dark:text-bw-muted mt-0.5">
                    {option.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Info note */}
          <div className="flex items-start gap-3 p-3 bg-fit-moderate-bg dark:bg-amber-900/20 rounded-lg text-fit-moderate dark:text-amber-200 text-sm">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              {isSection
                ? 'The AI will enhance all deliverables based on your focus areas and feedback. This may take a moment for large deliverable sets.'
                : 'The AI will enhance this deliverable with more realistic estimates, better specifications, and improved compliance coverage.'
              }
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="px-6 py-4 bg-bw-surface-subtle dark:bg-gray-800/50 border-t border-bw-border dark:border-gray-700 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-bw-ink dark:text-gray-300 bg-bw-surface dark:bg-gray-700 border border-bw-border dark:border-gray-600 rounded-lg hover:bg-bw-surface-subtle dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={(focusAreas.length === 0 && !feedback.trim()) || isSubmitting}
            className="px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Enhancing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Enhance {isSection ? 'Section' : 'Deliverable'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeliverableFeedbackDialog;
